import {
	debounce,
	defaultPageSize,
	getLocalStorageData,
	handleSort,
} from "helpers/utils";
import UsersListTable from "./UsersListTable";
import { CircularProgress } from "@mui/material";
import ColourConstants from "helpers/colourConstants";
import { makeStyles } from "tss-react/mui";
import Typography from "@mui/material/Typography";
import {
	downloadUserCSVTemplate,
	getClientAdminUserList,
	getClientAdminUserListCount,
	getSiteAppUserList,
	getSiteAppUserListCount,
	getUsersList,
	getUsersListCount,
	resendInvitation,
} from "services/users/usersList";
import { useState, useCallback, useMemo, useEffect } from "react";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import ContentStyle from "styles/application/ContentStyle";
import { Grid } from "@mui/material";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";

import AddUserDialog from "./AddUserDialog";
import DeleteDialog from "components/Elements/DeleteDialog";

import GeneralButton from "components/Elements/GeneralButton";
import mainAccess from "helpers/access";
import ImportContainer from "components/Modules/ImportContainer";
import { Apis } from "services/api";
import TabTitle from "components/Elements/TabTitle";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { REMOVE } from "constants/UserConstants/indes";
import roles from "helpers/roles";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import {
	makeGetNextPageParam,
	flattenPages,
} from "helpers/infiniteQuery";

const AC = ContentStyle();

const media = "@media(max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	listActions: {
		marginBottom: 30,
	},
	headerContainer: {
		display: "flex",
		[media]: {
			flexDirection: "column",
		},
	},
	headerText: {
		fontSize: 21,
	},
	buttonContainer: {
		display: "flex",
		marginLeft: "auto",
		[media]: {
			marginLeft: 0,
		},
	},
	importButton: {
		background: "#ED8738",
	},
	productButton: {
		backgroundColor: ColourConstants.confirmButton,
		color: "#FFFFFF",
		fontFamily: "Roboto Condensed",
		width: 150,
		fontSize: "13.5px",
		fontWeight: "bold",
		marginRight: "10px",
	},
	restore: {
		border: "2px solid",
		borderRadius: "100%",
		height: "35px",
		width: "35px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#307ad6",
	},
}));

const USERS_LIST_KEY = ["users", "list"];
const USERS_COUNT_KEY = ["users", "count"];

const UsersListContentInternal = ({ getError }) => {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const { filterValues, allFiltersInitialized, buildUrlParams } = useFilters();
	const queryClient = useQueryClient();
	const {
		position,
		role,
		siteAppID,
		siteID,
		isSiteUser,
		customCaptions,
		application,
		adminType,
	} = getLocalStorageData("me");
	const clientUserId = getLocalStorageData("clientUserId");

	const [currentTableSort, setCurrentTableSort] = useState([
		"firstName",
		"asc",
	]);
	const [searchTxt, setSearchTxt] = useState("");
	const [user, setUser] = useState("");

	const access = position?.[mainAccess.userAccess];
	const dispatch = useDispatch();

	const [modal, setModal] = useState({ import: false, add: false });

	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	// CSV import / delete API endpoints by role.
	const apis = {
		SiteUser: {
			downloadTemplate: Apis.Applications,
			upload: `${Apis.Applications}/${siteAppID}/uploadUserList`,
			import: `${Apis.Applications}/${siteAppID}/importusers`,
			delete: Apis.ClientUserSiteApps,
		},
		ClientAdmin: {
			downloadTemplate: Apis.Clients,
			upload: `${Apis.Clients}/${clientUserId}/uploadUserList`,
			import: `${Apis.Clients}/${clientUserId}/importusers`,
			delete: Apis.userDetailSites,
		},
		SuperAdmin: {
			downloadTemplate: Apis.UsersList,
			upload: `${Apis.UsersList}/uploadUserList`,
			import: `${Apis.UsersList}/importusers`,
			delete: Apis.UsersList,
		},
	};

	const [pageSize] = useState(() => defaultPageSize());

	const urlParams = buildUrlParams();
	const urlParamsKey = JSON.stringify(urlParams);

	const apiContext = useMemo(() => {
		if (siteAppID) return "siteApp";
		if (role === roles.clientAdmin) return "clientAdmin";
		if (role === roles.superAdmin) return "superAdmin";
		return null;
	}, [siteAppID, role]);

	const queryInputs = useMemo(
		() => ({
			apiContext,
			siteAppID: siteAppID || null,
			clientUserId: clientUserId || null,
			search: searchTxt,
			sortOrder: currentTableSort[1],
			sortField: currentTableSort[0],
			siteDepartmentIds: urlParams.siteDepartmentIds || [],
			siteIds: urlParams.siteIds || [],
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			apiContext,
			siteAppID,
			clientUserId,
			searchTxt,
			currentTableSort,
			urlParamsKey,
		]
	);

	const fetchListPage = useCallback(
		async (pageNumber) => {
			const { apiContext: ctx, siteAppID: appId, clientUserId: cuid, search, sortOrder, sortField, siteDepartmentIds, siteIds } = queryInputs;
			let response;
			if (ctx === "siteApp") {
				response = await getSiteAppUserList(
					appId,
					pageNumber,
					pageSize,
					search,
					sortOrder,
					sortField,
					siteDepartmentIds
				);
			} else if (ctx === "clientAdmin") {
				response = await getClientAdminUserList(
					cuid,
					pageNumber,
					pageSize,
					search,
					sortOrder,
					sortField,
					siteIds
				);
			} else if (ctx === "superAdmin") {
				response = await getUsersList(
					pageNumber,
					pageSize,
					search,
					sortOrder,
					sortField
				);
			} else {
				return [];
			}
			if (!response?.status) {
				throw new Error(`Failed to load ${customCaptions?.userPlural || "users"}`);
			}
			return (response.data || []).map((u) => ({
				...u,
				departmentName: u.departmentName || t("option.allDepartments"),
			}));
		},
		[queryInputs, pageSize, customCaptions?.userPlural, t]
	);

	const fetchListCount = useCallback(async () => {
		const { apiContext: ctx, siteAppID: appId, clientUserId: cuid, search, siteDepartmentIds, siteIds } = queryInputs;
		let response;
		if (ctx === "siteApp") {
			response = await getSiteAppUserListCount(appId, search, siteDepartmentIds);
		} else if (ctx === "clientAdmin") {
			response = await getClientAdminUserListCount(cuid, search, siteIds);
		} else if (ctx === "superAdmin") {
			response = await getUsersListCount(search);
		} else {
			return 0;
		}
		return response?.status ? response.data : 0;
	}, [queryInputs]);

	const usersQueryKey = useMemo(
		() => [...USERS_LIST_KEY, { ...queryInputs, pageSize }],
		[queryInputs, pageSize]
	);

	const usersQuery = useInfiniteQuery({
		queryKey: usersQueryKey,
		enabled: allFiltersInitialized && Boolean(queryInputs.apiContext),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const rows = await fetchListPage(pageParam);
			return { rows, pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	const countQuery = useQuery({
		queryKey: [...USERS_COUNT_KEY, queryInputs],
		enabled: allFiltersInitialized && Boolean(queryInputs.apiContext),
		queryFn: fetchListCount,
	});

	const allData = useMemo(() => flattenPages(usersQuery.data), [usersQuery.data]);
	const count = countQuery.data;
	const haveData = !usersQuery.isLoading;

	const refreshUsers = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
		return queryClient.invalidateQueries({ queryKey: USERS_COUNT_KEY });
	}, [queryClient]);

	useEffect(() => {
		if (usersQuery.isError) {
			dispatch(
				showError(
					t("message.failedToLoadEntity", {
						entity: customCaptions?.userPlural || "users",
					})
				)
			);
		}
	}, [usersQuery.isError, dispatch, customCaptions?.userPlural, t]);

	const handleAddData = () => {
		refreshUsers();
	};

	const handleDeleteDialogOpen = (rowData) => {
		setUser(`${rowData?.firstName} ${rowData?.lastName}`);
		if (role === roles.clientAdmin && !isSiteUser)
			setDeleteID(rowData.clientUserID);

		if (role === roles.superAdmin) setDeleteID(rowData.id);
		if (role === roles.siteUser || isSiteUser)
			setDeleteID(rowData.clientUserSiteAppID);

		setOpenDeleteDialog(true);
	};

	const closeDeleteDialog = () => setOpenDeleteDialog(false);

	const handleRemoveData = () => {
		refreshUsers();
	};

	const importSuccess = () => {
		refreshUsers();
	};

	const handleSearch = useCallback(
		debounce((value) => setSearchTxt(value), 500),
		[]
	);

	const handleResendInvitation = async (val) => {
		try {
			const result = await resendInvitation({ email: val.email });
			if (!result.status) {
				dispatch(showError(result.data || t("message.failedToSendInvitation")));
			}
		} catch (err) {
			dispatch(showError(err?.data || t("message.failedToSendInvitation")));
		}
	};

	const handleDownloadCsvTemplate = () => {
		return downloadUserCSVTemplate(apis[role].downloadTemplate);
	};
	const apiByRole =
		role === roles.siteUser || (role === roles.clientAdmin && isSiteUser)
			? apis[roles.siteUser]
			: role === roles.clientAdmin && !isSiteUser
			? apis[roles.clientAdmin]
			: apis[roles.superAdmin];

	const userRole =
		role === roles.superAdmin
			? t("label.superadmin")
			: role === roles.clientAdmin && !siteAppID
			? t("label.client")
			: t("label.application");

	return (
		<div className="container">
			{!application ? (
				<TabTitle title={t("entity.user_other")} />
			) : (
				<TabTitle
					title={`${customCaptions?.userPlural} | ${application?.name}`}
				/>
			)}

			<ImportContainer
				open={modal.import}
				handleClose={() => setModal((th) => ({ ...th, import: false }))}
				importSuccess={importSuccess}
				siteAppID={siteAppID}
				importApi={apis[role].import}
				uploadApi={apis[role].upload}
				handleDownloadTemplate={handleDownloadCsvTemplate}
			/>

			<AddUserDialog
				open={modal.add}
				handleClose={() => setModal((th) => ({ ...th, add: false }))}
				handleAddData={handleAddData}
				setSearchQuery={() => {}}
				getError={getError}
				role={role}
				isSiteUser={isSiteUser}
				siteID={siteID}
				siteAppID={siteAppID}
				customCaptions={customCaptions}
				adminType={adminType}
			/>

			<DeleteDialog
				entityName="User"
				open={openDeleteDialog}
				closeHandler={closeDeleteDialog}
				deleteID={deleteID}
				deleteEndpoint={apiByRole.delete}
				handleRemoveData={handleRemoveData}
				deleteButton={REMOVE}
				deleteMsg={t("message.userWillLoseAccess", {
					user,
					accountType: userRole,
				})}
			/>

			<div className={classes.listActions}>
				<div className={classes.headerContainer}>
					<Typography
						className={classes.headerText}
						component="h1"
						gutterBottom
					>
						{!count ? (
							<strong>
								{t("label.entityList", {
									entity: customCaptions?.userPlural ?? "Users",
								})}
							</strong>
						) : (
							<strong>
								{t("label.entityListCount", {
									entity: customCaptions?.userPlural ?? "Users",
									count,
								})}
							</strong>
						)}
					</Typography>
					{haveData && (access === FULL_ACCESS || !siteAppID) ? (
						<div className={classes.buttonContainer}>
							{role !== roles.superAdmin && (
								<GeneralButton
									onClick={() => setModal((th) => ({ ...th, import: true }))}
									style={{ backgroundColor: "#ed8738" }}
								>
									{t("action.importFromList")}
								</GeneralButton>
							)}
							<GeneralButton
								onClick={() => setModal((th) => ({ ...th, add: true }))}
								style={{ backgroundColor: "#23bb79" }}
							>
								{t("action.addNew")}
							</GeneralButton>
						</div>
					) : null}
				</div>

				{haveData ? (
					<AC.SearchContainer>
						<AC.SearchInner className="applicationSearchBtn">
							<Grid container spacing={1} alignItems="flex-end">
								<Grid item>
									<SearchIcon />
								</Grid>
								<Grid item>
									<AC.SearchInput
										variant="standard"
										onChange={(e) => handleSearch(e.target.value)}
										label={t("placeholder.search")}
									/>
								</Grid>
							</Grid>
						</AC.SearchInner>
					</AC.SearchContainer>
				) : null}

				{/* Filter Framework - Show for Site App users and Client Admin users */}
				{haveData && (siteAppID || (role === roles.clientAdmin && !siteAppID)) && (
					<div style={{ marginTop: 20 }}>
						<FilterContainer />
					</div>
				)}
			</div>

			{haveData ? (
				<UsersListTable
					data={allData}
					position={position}
					access={access}
					headers={
						siteID
							? [
									t("label.firstName"),
									t("label.surname"),
									t("label.emailAddress"),
									`${customCaptions?.department ?? "Department"}`,
									`${customCaptions?.position ?? "Position"}`,
									t("label.status"),
									...(siteAppID ? [`${(customCaptions?.userReference || customCaptions?.userReferencePlural) ?? "Reference Number"}`] : []),
							  ]
							: role === roles.superAdmin
							? [
									t("label.firstName"),
									t("label.surname"),
									t("label.emailAddress"),
									t("entity.type"),
									t("label.status"),
							  ]
							: [
									t("label.firstName"),
									t("label.surname"),
									t("label.emailAddress"),
									t("label.status"),
									t("label.clientAdministrator"),
							  ]
					}
					columns={
						siteID
							? [
									"firstName",
									"lastName",
									"email",
									"departmentName",
									"positionName",
									"active",
									...(siteAppID ? ["externalReference"] : []),
							  ]
							: role === roles.superAdmin
							? ["firstName", "lastName", "email", "Type", "active"]
							: ["firstName", "lastName", "email", "active", "isAdmin"]
					}
					handleSort={handleSort}
					handleDeleteDialogOpen={handleDeleteDialogOpen}
					setCurrentTableSort={setCurrentTableSort}
					currentTableSort={currentTableSort}
					handleResendInvitation={handleResendInvitation}
					hasNextPage={usersQuery.hasNextPage}
					isFetchingNextPage={usersQuery.isFetchingNextPage}
					fetchNextPage={usersQuery.fetchNextPage}
					isFetchNextPageError={usersQuery.isFetchNextPageError}
				/>
			) : (
				<CircularProgress />
			)}
		</div>
	);
};

// Wrapper component that provides FilterContext
function UsersListContent(props) {
	const { role, siteAppID } = getLocalStorageData("me") || {};
	const clientUserId = getLocalStorageData("clientUserId");

	const filterConfig = {
		filters: siteAppID
			? { department: { multiSelect: true } }
			: (role === roles.clientAdmin ? { site: { multiSelect: true } } : {})
	};

	const storageKey = siteAppID ? "users" : "clientUsers";
	const persistenceId = siteAppID || clientUserId;

	return (
		<FilterProvider
			config={filterConfig}
			storageKey={storageKey}
			siteAppId={persistenceId}
		>
			<UsersListContentInternal {...props} />
		</FilterProvider>
	);
}

export default UsersListContent;
